import { earthRadius, tileSize } from "@churchofjesuschrist/maps-constants";
import { AreaUnitType, LengthUnitType, BoundingBox, Position } from "@churchofjesuschrist/maps-models";
export { AreaUnitType, LengthUnitType };
export const toDegrees = radians => {
  return radians * 180.0 / Math.PI;
};
export const toRadians = degrees => {
  return degrees * Math.PI / 180.0;
};
export const toDecimalDegrees = (point, radius = earthRadius) => {
  let longitude = toDegrees(point.x / radius),
    latitude = toDegrees(Math.atan(Math.pow(Math.E, point.y / radius)) * 2 - Math.PI / 2);
  return {
    latitude,
    longitude
  };
};
export const toMercatorMeters = (position, radius = earthRadius) => {
  let pos = Position.toJSON(position);
  let x = toRadians(pos.longitude) * radius,
    y = Math.log(Math.tan((toRadians(pos.latitude) + Math.PI / 2) / 2)) * radius;
  return {
    x,
    y
  };
};
export const toPixels = (position, tileSize = tileSize) => {
  let pos = Position.toJSON(position);
  let siny = Math.sin(pos.latitude * Math.PI / 180);
  // Truncating to 0.9999 effectively limits latitude to 89.189. This is about a third of a tile past the edge of the world tile.
  siny = Math.min(Math.max(siny, -0.9999), 0.9999);
  let x = tileSize * (0.5 + position.longitude / 360),
    y = tileSize * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI));
  return {
    x,
    y
  };
};

// https://developers.google.com/maps/documentation/javascript/examples/map-coordinates
export const toTileCoordinates = (position, zoom, tileSize = tileSize) => {
  let scale = 1 << zoom,
    worldCoordinate = toPixels(position, tileSize);
  let x = Math.floor(worldCoordinate.x * scale / tileSize),
    y = Math.floor(worldCoordinate.y * scale / tileSize);
  return {
    x,
    y
  };
};
export const crossesAntiMeridian = (positionA, positionB) => {
  let posA = Position.toJSON(positionA),
    posB = Position.toJSON(positionB);
  return Math.abs(posA.longitude - posB.longitude) > 180 && (posA.longitude < 0 && posB.longitude > 0 || posA.longitude > 0 && posB.longitude < 0);
};
export const someCrossesAntiMeridian = positions => {
  let previous = positions[positions.length - 1];
  for (let i = 0; i < positions.length; i++) {
    let position = positions[i];
    if (crossesAntiMeridian(position, previous)) {
      return true;
    }
    previous = position;
  }
  return false;
};
export const isClockwise = positions => {
  let sum = 0;
  for (let i = 0; i < positions.length; i++) {
    let p1 = Position.toJSON(positions[i]),
      p2 = Position.toJSON(positions[(i + 1) % positions.length]);
    sum += (p2.longitude - p1.longitude) * (p2.latitude + p1.latitude);
  }
  return sum >= 0;
};
export const isPointInRange = (origin, point, range) => {
  //let size = Size.toJSON(range);    // maybe mode Size to geojson library, maybe rename to geometry library?
  //TODO: consider range to be range.width or range if is number on own
  return Math.abs(origin.x - point.x) <= range.width && Math.abs(origin.y - point.y) <= range.height;
};
export const computePointOffset = (distance, heading, origin = null) => {
  let rHeading = toRadians(heading);
  let x = distance * Math.sin(rHeading);
  let y = distance * Math.cos(rHeading);
  if (origin) {
    x += origin.x;
    y += origin.y;
  }
  return {
    x,
    y
  };
};
export const getPowerScale = (zoom, base = 0.1) => {
  return Math.min(Math.max(Math.pow(base * (zoom + 1), 2) + 0.01, 0.125), 1);
};
export const getMarkerScale = (() => {
  const cache = {};
  return (zoom, base = 1) => {
    const key = `${zoom}:${base}`;
    let value = cache[key];
    if (!value) {
      value = cache[key] = getPowerScale(zoom) * base;
    }
    return value;
  };
})();
export const getLowestZoomLevel = (() => {
  const latRad = lat => {
    let sin = Math.sin(lat * Math.PI / 180.0),
      radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };
  const zoom = (size, fraction) => {
    return Math.floor(Math.log(size / tileSize / fraction) / Math.LN2);
  };
  return (boundingBox, size) => {
    let bbox = BoundingBox.toJSON(boundingBox);
    let latFraction = (latRad(bbox.northEast.latitude) - latRad(bbox.southWest.latitude)) / Math.PI;
    let lngDiff = bbox.northEast.longitude - bbox.southWest.longitude,
      lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    let latZoom = zoom(size.height, latFraction),
      lngZoom = zoom(size.width, lngFraction);
    return Math.min(latZoom, lngZoom, 21);
  };
})();
export const roundTo = (value, precision) => {
  return parseFloat(value.toFixed(precision));
};
export const pushLongitude = position => {
  position = Position.toJSON(position);
  if (position.longitude < 0) {
    position = {
      ...position,
      longitude: position.longitude + 360
    };
  }
  return position;
};
export const pushAllLongitude = positions => {
  return positions.map(pushLongitude);
};
export const closestPositionOnSegment = (vertexA, vertexB, position) => {
  vertexA = Position.toJSON(vertexA);
  vertexB = Position.toJSON(vertexB);
  position = Position.toJSON(position);
  let closest, distance;

  // if crosses 180 line
  if (crossesAntiMeridian(vertexA, vertexB)) {
    vertexA = pushLongitude(vertexA);
    vertexB = pushLongitude(vertexB);
    position = pushLongitude(position);
  }
  let pointA = toMercatorMeters(vertexA),
    pointB = toMercatorMeters(vertexB),
    pointP = toMercatorMeters(position);
  if (pointA.x !== pointB.x) {
    let a = (pointA.y - pointB.y) / (pointA.x - pointB.x),
      b = pointA.y - a * pointA.x;
    distance = Math.abs(a * pointP.x + b - pointP.y) / Math.sqrt(a * a + 1);
  } else {
    distance = Math.abs(pointP.x - pointA.x);
  }

  // make sure distance is within line segment
  let dAB2 = Math.pow(pointA.y - pointB.y, 2) + Math.pow(pointA.x - pointB.x, 2),
    dPA2 = Math.pow(pointA.y - pointP.y, 2) + Math.pow(pointA.x - pointP.x, 2),
    dPB2 = Math.pow(pointB.y - pointP.y, 2) + Math.pow(pointB.x - pointP.x, 2),
    d2 = Math.pow(distance, 2),
    calc_dAB2 = dPA2 - d2 + dPB2 - d2;
  // redefine minimum distance to line segment (not infinite line) if necessary
  if (calc_dAB2 > dAB2) {
    if (dPA2 < dPB2) {
      closest = vertexA;
      //distance = Math.sqrt(dTA2);
    } else {
      closest = vertexB;
      //distance = Math.sqrt(dTB2);
    }
  } else {
    let fraction = Math.sqrt(dPA2 - d2) / Math.sqrt(dAB2),
      x = pointA.x - (pointA.x - pointB.x) * fraction,
      y = pointA.y - (pointA.y - pointB.y) * fraction;
    closest = toDecimalDegrees({
      x,
      y
    });
  }
  return closest;
};
export const convertUnit = (value, fromType, toType) => {
  if (fromType && toType && fromType !== toType) {
    let fromRatio = unitBaseRatio(fromType),
      toRatio = unitBaseRatio(toType);
    value /= fromRatio;
    value *= toRatio;
  }
  return value;
};
export const unitBaseRatio = unitType => {
  switch (unitType) {
    case LengthUnitType.MILLIMETER:
      return 1000;
    case LengthUnitType.CENTIMETER:
      return 100;
    case LengthUnitType.DECIMETER:
      return 10;
    case LengthUnitType.METER:
      return 1;
    case LengthUnitType.DEKAMETER:
      return 0.1;
    case LengthUnitType.HECTOMETER:
      return 0.01;
    case LengthUnitType.KILOMETER:
      return 0.001;
    case LengthUnitType.INCH:
      return 39.37007874;
    case LengthUnitType.FOOT:
      return 3.280839895;
    case LengthUnitType.YARD:
      return 1.0936133;
    case LengthUnitType.MILE:
      return 0.00062137119224;
    case AreaUnitType.SQUARE_MILLIMETER:
      return 1000000;
    case AreaUnitType.SQUARE_CENTIMETER:
      return 10000;
    case AreaUnitType.SQUARE_DECIMETER:
      return 100;
    case AreaUnitType.SQUARE_METER:
      return 1;
    case AreaUnitType.SQUARE_DEKAMETER:
      return 0.01;
    case AreaUnitType.SQUARE_HECTOMETER:
      return 0.0001;
    case AreaUnitType.SQUARE_KILOMETER:
      return 0.000001;
    case AreaUnitType.SQUARE_INCH:
      return 1550.0031000062;
    case AreaUnitType.SQUARE_FOOT:
      return 10.763910417;
    case AreaUnitType.SQUARE_YARD:
      return 1.1959900463011;
    case AreaUnitType.SQUARE_MILE:
      return 0.00000038610215854245;
    case AreaUnitType.ACRE:
      return 0.00024710538146717;
  }
  return null;
};